import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Button,
  Divider,
  Form,
  Grid,
  Header,
  Image,
  Message,
  Modal,
  Card,
} from 'semantic-ui-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../context/User';
import {
  API,
  showError,
  showFormalPricingWarningsV87,
  showSuccess,
  showWarning,
} from '../helpers';
import { onGitHubOAuthClicked, onLarkOAuthClicked } from './utils';
import larkIcon from '../images/lark.svg';
import { getPasskeyV815 } from '../helpers/webauthnV815';
import { useSystemBrandingV9 } from '../context/SystemBrandingV9';
import { StatusContext } from '../context/Status';
import RegistrationPuzzleV108 from './RegistrationPuzzleV108';
import Turnstile from 'react-turnstile';
import { consumeClientLoginV110 } from '../helpers/clientLoginV110';

const LoginForm = () => {
  const { t, i18n } = useTranslation();
  const text = (zh, en) => (i18n?.language?.startsWith('zh') ? zh : en);
  const [inputs, setInputs] = useState({
    username: '',
    password: '',
    wechat_verification_code: '',
    two_factor_code: '',
  });
  const [searchParams] = useSearchParams();
  const [show2FA, setShow2FA] = useState(false);
  const [mfaMethods, setMfaMethods] = useState([]);
  const { username, password, two_factor_code } = inputs;
  const [, userDispatch] = useContext(UserContext);
  let navigate = useNavigate();
  const statusContext = useContext(StatusContext);
  const liveStatus = Array.isArray(statusContext)
    ? statusContext[0]?.status
    : undefined;
  const [status, setStatus] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('status') || '{}') || {};
    } catch (_) {
      return {};
    }
  });
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaRevision, setCaptchaRevision] = useState(0);
  const [captchaError, setCaptchaError] = useState(false);
  const [loading, setLoading] = useState(false);
  const pending = useRef(false);
  const mounted = useRef(true);
  const provider =
    status.login_captcha_provider === 'turnstile' ? 'turnstile' : 'local';
  const captchaConfig = `${provider}:${status.turnstile_site_key || ''}`;
  const previousCaptchaConfig = useRef(captchaConfig);
  const loginEnabled = status.password_login_enabled !== false;
  const { logo, onLogoError } = useSystemBrandingV9();

  useEffect(() => {
    mounted.current = true;
    if (searchParams.get('expired')) {
      showError(t('messages.error.login_expired'));
    }
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (liveStatus) setStatus(liveStatus);
  }, [liveStatus]);

  useEffect(() => {
    if (previousCaptchaConfig.current === captchaConfig) return;
    previousCaptchaConfig.current = captchaConfig;
    setCaptchaToken('');
    setCaptchaError(false);
    setCaptchaRevision((value) => value + 1);
  }, [captchaConfig]);

  const [showWeChatLoginModal, setShowWeChatLoginModal] = useState(false);

  const onWeChatLoginClicked = () => {
    setShowWeChatLoginModal(true);
  };

  const onSubmitWeChatVerificationCode = async () => {
    const res = await API.get(
      `/api/oauth/wechat?code=${inputs.wechat_verification_code}`
    );
    if (!res?.data) return;
    const { success, message, data, pricing_warnings } = res.data;
    if (success) {
      userDispatch({ type: 'login', payload: data });
      localStorage.setItem('user', JSON.stringify(data));
      showFormalPricingWarningsV87(pricing_warnings, t);
      navigate(data.account_kind_v110 === 'staff' ? '/staff' : consumeClientLoginV110('/'));
      showSuccess(t('messages.success.login'));
      setShowWeChatLoginModal(false);
    } else {
      showError(message);
    }
  };

  function handleChange(e) {
    const { name, value } = e.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
    if (name === 'username' || name === 'password') {
      setShow2FA(false);
      setMfaMethods([]);
      setInputs((current) => ({ ...current, two_factor_code: '' }));
    }
  }

  function resetCaptcha() {
    setCaptchaToken('');
    setCaptchaError(false);
    setCaptchaRevision((value) => value + 1);
  }

  function beginAttempt() {
    if (pending.current || !loginEnabled || !username || !password)
      return false;
    if (!captchaToken) {
      showError(text('请先完成人机验证', 'Complete human verification first'));
      return false;
    }
    pending.current = true;
    setLoading(true);
    return true;
  }

  const captchaHeaders = () => ({
    headers:
      provider === 'local'
        ? { 'X-Login-Captcha': captchaToken }
        : { 'X-Turnstile-Token': captchaToken },
  });

  function finishAttempt() {
    pending.current = false;
    if (mounted.current) {
      setLoading(false);
      resetCaptcha();
    }
  }

  const completeLoginV815 = (data, pricingWarnings) => {
    userDispatch({ type: 'login', payload: data });
    localStorage.setItem('user', JSON.stringify(data));
    showFormalPricingWarningsV87(pricingWarnings, t);
    if (username === 'root' && password === '123456') {
      navigate('/user/edit');
      showSuccess(t('messages.success.login'));
      showWarning(t('messages.error.root_password'));
    } else {
      navigate(data.account_kind_v110 === 'staff' ? '/staff' : consumeClientLoginV110('/token'));
      showSuccess(t('messages.success.login'));
    }
  };

  async function handleSubmit(e) {
    e?.preventDefault();
    if (!beginAttempt()) return;
    try {
      const res = await API.post(
        '/api/user/login',
        {
          username,
          password,
          two_factor_code,
        },
        captchaHeaders()
      );
      if (!mounted.current) return;
      if (!res?.data)
        throw new Error(text('登录失败，请重试', 'Login failed. Try again.'));
      const { success, message, data, pricing_warnings } = res.data;
      if (success) {
        if (message === 'mfa_required' || message === '2fa_required') {
          const methods = data?.methods || ['totp'];
          setMfaMethods(methods);
          setShow2FA(methods.includes('totp'));
          return;
        }
        completeLoginV815(data, pricing_warnings);
      } else
        throw new Error(
          message || text('登录失败，请重试', 'Login failed. Try again.')
        );
    } catch (error) {
      if (mounted.current)
        showError(error.response?.data?.message || error.message);
    } finally {
      finishAttempt();
    }
  }

  const handlePasskeyLoginV815 = async () => {
    if (!beginAttempt()) return;
    try {
      const begin = await API.post(
        '/api/user/passkey/login/begin',
        {
          username,
          password,
        },
        captchaHeaders()
      );
      if (!mounted.current) return;
      if (!begin?.data?.success)
        throw new Error(
          begin?.data?.message ||
            text('通行密钥验证失败', 'Passkey verification failed')
        );
      const ceremonyId = begin.data.data.ceremony_id;
      const credential = await getPasskeyV815(begin.data.data.options);
      if (!mounted.current) return;
      const finish = await API.post(
        '/api/user/passkey/login/finish?ceremony_id=' +
          encodeURIComponent(ceremonyId),
        credential
      );
      if (!mounted.current) return;
      if (!finish?.data?.success)
        throw new Error(
          finish?.data?.message ||
            text('通行密钥验证失败', 'Passkey verification failed')
        );
      completeLoginV815(finish.data.data, finish.data.pricing_warnings);
    } catch (error) {
      if (mounted.current)
        showError(error.response?.data?.message || error.message);
    } finally {
      finishAttempt();
    }
  };

  return (
    <Grid textAlign='center' style={{ marginTop: '48px' }}>
      <Grid.Column style={{ maxWidth: 450 }}>
        <Card
          fluid
          className='chart-card'
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}
        >
          <Card.Content>
            <Card.Header>
              <Header
                as='h2'
                textAlign='center'
                style={{ marginBottom: '1.5em' }}
              >
                <Image
                  src={logo}
                  onError={onLogoError}
                  style={{ marginBottom: '10px' }}
                />
                <Header.Content>{t('auth.login.title')}</Header.Content>
              </Header>
            </Card.Header>
            <Form size='large' onSubmit={handleSubmit}>
              <Form.Input
                fluid
                icon='user'
                iconPosition='left'
                placeholder={t('auth.login.username')}
                name='username'
                value={username}
                autoComplete='username'
                disabled={loading || !loginEnabled}
                onChange={handleChange}
                style={{ marginBottom: '1em' }}
              />
              <Form.Input
                fluid
                icon='lock'
                iconPosition='left'
                placeholder={t('auth.login.password')}
                name='password'
                type='password'
                value={password}
                autoComplete='current-password'
                disabled={loading || !loginEnabled}
                onChange={handleChange}
                style={{ marginBottom: '1.5em' }}
              />
              {show2FA && mfaMethods.includes('totp') && (
                <Form.Input
                  fluid
                  icon='shield alternate'
                  iconPosition='left'
                  placeholder={t('auth.login.two_factor_code') || '两步验证码'}
                  name='two_factor_code'
                  value={two_factor_code}
                  autoComplete='one-time-code'
                  inputMode='numeric'
                  disabled={loading || !loginEnabled}
                  onChange={handleChange}
                  style={{ marginBottom: '1.5em' }}
                />
              )}
              {!loginEnabled ? (
                <Message
                  warning
                  visible
                  role='status'
                  content={text('密码登录已关闭', 'Password login is disabled')}
                />
              ) : provider === 'local' ? (
                <RegistrationPuzzleV108
                  purpose='login'
                  revision={captchaRevision}
                  onChange={setCaptchaToken}
                />
              ) : (
                <div
                  role='group'
                  aria-label={text('人机验证', 'Human verification')}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 20,
                  }}
                >
                  {status.turnstile_site_key ? (
                    <Turnstile
                      key={`${status.turnstile_site_key}-${captchaRevision}`}
                      sitekey={status.turnstile_site_key}
                      action='login'
                      size='compact'
                      theme='auto'
                      onVerify={(token) => {
                        setCaptchaToken(token);
                        setCaptchaError(false);
                      }}
                      onExpire={() => setCaptchaToken('')}
                      onError={() => {
                        setCaptchaToken('');
                        setCaptchaError(true);
                      }}
                      onTimeout={() => {
                        setCaptchaToken('');
                        setCaptchaError(true);
                      }}
                      onUnsupported={() => {
                        setCaptchaToken('');
                        setCaptchaError(true);
                      }}
                    />
                  ) : (
                    <Message
                      warning
                      visible
                      role='alert'
                      content={text(
                        '人机验证配置未完成，请联系管理员',
                        'Human verification is not configured. Contact an administrator.'
                      )}
                    />
                  )}
                  {captchaError && (
                    <Message warning visible role='alert'>
                      {text(
                        '人机验证未完成，请重试',
                        'Human verification failed. Retry.'
                      )}
                      <Button
                        type='button'
                        basic
                        icon='refresh'
                        title={text('重试验证', 'Retry verification')}
                        aria-label={text('重试验证', 'Retry verification')}
                        onClick={resetCaptcha}
                      />
                    </Message>
                  )}
                </div>
              )}
              <Button
                fluid
                size='large'
                type='submit'
                disabled={
                  loading ||
                  !loginEnabled ||
                  !username ||
                  !password ||
                  !captchaToken
                }
                loading={loading}
                style={{
                  background: '#2F73FF', // 使用更现代的蓝色
                  color: 'white',
                  marginBottom: '1.5em',
                }}
              >
                {t('auth.login.button')}
              </Button>
              {mfaMethods.includes('passkey') && (
                <Button
                  fluid
                  basic
                  primary
                  type='button'
                  disabled={loading || !loginEnabled || !captchaToken}
                  icon='key'
                  content={text('使用 Passkey', 'Use Passkey')}
                  onClick={handlePasskeyLoginV815}
                />
              )}
            </Form>

            <Divider />
            <Message style={{ background: 'transparent', boxShadow: 'none' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '8px 16px',
                  fontSize: '0.9em',
                  color: '#666',
                }}
              >
                <div>
                  {t('auth.login.forgot_password')}
                  <Link
                    to='/reset'
                    style={{ color: '#2185d0', marginLeft: '2px' }}
                  >
                    {t('auth.login.reset_password')}
                  </Link>
                </div>
                <div>
                  {t('auth.login.no_account')}
                  <Link
                    to='/register'
                    style={{ color: '#2185d0', marginLeft: '2px' }}
                  >
                    {t('auth.login.register')}
                  </Link>
                </div>
              </div>
            </Message>

            {(status.github_oauth ||
              status.wechat_login ||
              status.lark_client_id) && (
              <>
                <Divider
                  horizontal
                  style={{ color: '#666', fontSize: '0.9em' }}
                >
                  {t('auth.login.other_methods')}
                </Divider>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '1em',
                    marginTop: '1em',
                  }}
                >
                  {status.github_oauth && (
                    <Button
                      circular
                      color='black'
                      icon='github'
                      onClick={() =>
                        onGitHubOAuthClicked(status.github_client_id)
                      }
                    />
                  )}
                  {status.wechat_login && (
                    <Button
                      circular
                      color='green'
                      icon='wechat'
                      onClick={onWeChatLoginClicked}
                    />
                  )}
                  {status.lark_client_id && (
                    <div
                      style={{
                        background:
                          'radial-gradient(circle, #FFFFFF, #FFFFFF, #FFFFFF, #FFFFFF, #FFFFFF)',
                        width: '36px',
                        height: '36px',
                        borderRadius: '10em',
                        display: 'flex',
                        cursor: 'pointer',
                      }}
                      onClick={() => onLarkOAuthClicked(status.lark_client_id)}
                    >
                      <Image
                        src={larkIcon}
                        avatar
                        style={{
                          width: '36px',
                          height: '36px',
                          cursor: 'pointer',
                          margin: 'auto',
                        }}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </Card.Content>
        </Card>
        <Modal
          onClose={() => setShowWeChatLoginModal(false)}
          onOpen={() => setShowWeChatLoginModal(true)}
          open={showWeChatLoginModal}
          size={'mini'}
        >
          <Modal.Content>
            <Modal.Description>
              <Image src={status.wechat_qrcode} fluid />
              <div style={{ textAlign: 'center' }}>
                <p>{t('auth.login.wechat.scan_tip')}</p>
              </div>
              <Form size='large'>
                <Form.Input
                  fluid
                  placeholder={t('auth.login.wechat.code_placeholder')}
                  name='wechat_verification_code'
                  value={inputs.wechat_verification_code}
                  onChange={handleChange}
                />
                <Button
                  fluid
                  size='large'
                  style={{
                    background: '#2F73FF',
                    color: 'white',
                    marginBottom: '1.5em',
                  }}
                  onClick={onSubmitWeChatVerificationCode}
                >
                  {t('auth.login.button')}
                </Button>
              </Form>
            </Modal.Description>
          </Modal.Content>
        </Modal>
      </Grid.Column>
    </Grid>
  );
};

export default LoginForm;
